/**
 * Search Result Filter Hook
 * Filters search results based on selected date and provider availability
 */

import { useState, useEffect } from 'react';

const DATE_FILTER_LIMIT = 50;
const CONCURRENCY = 8;

/**
 * Promise pool utility to limit concurrent requests
 */
const pool = async (concurrency, items, asyncFn) => {
  const results = [];
  const workers = [];
  let index = 0;

  const worker = async () => {
    while (index < items.length) {
      const currentIndex = index++;
      const item = items[currentIndex];
      try {
        const result = await asyncFn(item, currentIndex);
        results[currentIndex] = result;
      } catch (error) {
        console.error(`Worker error for item ${currentIndex}:`, error);
        results[currentIndex] = { error: true };
      }
    }
  };

  // Start workers
  for (let i = 0; i < Math.min(concurrency, items.length); i++) {
    workers.push(worker());
  }

  await Promise.all(workers);
  return results;
};

/**
 * Hook to filter search results by selected date
 */
export const useDateFilteredSearch = (listings = [], selectedDate = null) => {
  // Ensure listings is always an array
  const safeListings = Array.isArray(listings) ? listings : [];
  
  const [filteredListings, setFilteredListings] = useState(safeListings);
  const [filteredOutCount, setFilteredOutCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const enabled = process.env.REACT_APP_DATE_FILTER_SEARCH_ENABLED === 'true';

  useEffect(() => {
    if (!enabled || !selectedDate || !safeListings.length) {
      setFilteredListings(safeListings);
      setFilteredOutCount(0);
      setError(null);
      return;
    }

    const filterByDate = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`🔍 Filtering ${safeListings.length} listings for date: ${selectedDate}`);
        
        // Limit to first N listings to avoid overwhelming the API
        const subset = safeListings.slice(0, DATE_FILTER_LIMIT);
        
        console.log(`📊 Checking availability for ${subset.length} providers...`);
        
        // Fetch availability for each listing's provider concurrently
        const availabilityFlags = await pool(CONCURRENCY, subset, async (listing) => {
          try {
            // Extract provider ID - adjust based on your data structure
            const providerId = listing.relationships?.author?.data?.id?.uuid || 
                              listing.attributes?.authorId ||
                              listing.author?.id?.uuid;
            
            if (!providerId) {
              console.warn('⚠️ No provider ID found for listing:', listing.id);
              return { providerId: null, busy: false };
            }

            const response = await fetch(`/api/availability/${providerId}`, {
              method: 'GET',
              credentials: 'include',
            });

            if (!response.ok) {
              console.warn(`⚠️ Failed to fetch availability for provider ${providerId}:`, response.status);
              return { providerId, busy: false }; // Fail open
            }

            const data = await response.json();
            const busy = (data.bookedDates || []).includes(selectedDate);
            
            return { providerId, busy };
            
          } catch (error) {
            console.error('❌ Error fetching availability:', error);
            return { providerId: 'unknown', busy: false }; // Fail open
          }
        });

        // Create set of busy provider IDs
        const busyProviderIds = new Set(
          availabilityFlags
            .filter(flag => flag.busy && flag.providerId)
            .map(flag => flag.providerId)
        );

        console.log(`📊 Found ${busyProviderIds.size} busy providers for ${selectedDate}`);

        // Filter out busy providers
        const filtered = listings.filter(listing => {
          const providerId = listing.relationships?.author?.data?.id?.uuid || 
                            listing.attributes?.authorId ||
                            listing.author?.id?.uuid;
          
          return !busyProviderIds.has(providerId);
        });

        const filteredCount = listings.length - filtered.length;
        
        console.log(`✅ Filtered out ${filteredCount} listings, ${filtered.length} remaining`);
        
        setFilteredListings(filtered);
        setFilteredOutCount(filteredCount);
        
      } catch (error) {
        console.error('❌ Date filtering failed:', error);
        setError(error);
        // Fail open - show original results
        setFilteredListings(safeListings);
        setFilteredOutCount(0);
      } finally {
        setLoading(false);
      }
    };

    filterByDate();
  }, [enabled, selectedDate, safeListings]);

  return {
    filteredListings,
    filteredOutCount,
    isFiltering: loading,
    error,
    enabled
  };
};

export default useDateFilteredSearch;
